import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Tenant,
  TenantCreate,
  TenantGetFiltersResponse,
  TenantGetResponse,
  TenantUpdate,
} from '../types/tenant.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';

const useTenantBasePath = () => {
  return useMemo(() => {
    return '/v1/sysadmin/tenants';
  }, []);
};

export const useTenantHooks = () => {
  const tenantBasePath = useTenantBasePath();

  return createApiHooks<Tenant, TenantGetResponse, TenantCreate, TenantUpdate>({
    basePath: tenantBasePath,
    queryKey: ['Tenants'],
    apiClient,
  });
};

export const useTenantGetFilters = (queryOptions?: any) => {
  const tenantBasePath = useTenantBasePath();
  return useGenericGet<TenantGetFiltersResponse>(
    `${tenantBasePath}/filters`,
    ['Tenants', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the tenant hooks
export const useTenantApi = () => {
  const tenantHooks = useTenantHooks();

  return {
    useGet: tenantHooks.useGet,
    useCreate: tenantHooks.useCreate,
    useUpdate: tenantHooks.useUpdate,
    useDelete: tenantHooks.useDelete,
    useList: (params: UseListParams) => tenantHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useTenantGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useTenantApi();
  return useGet(id, queryOptions);
};

export const useTenantCreate = (mutationOptions?: any) => {
  const { useCreate } = useTenantApi();
  return useCreate(mutationOptions);
};

export const useTenantUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useTenantApi();
  return useUpdate(mutationOptions);
};

export const useTenantDelete = (mutationOptions?: any) => {
  const { useDelete } = useTenantApi();
  return useDelete(mutationOptions);
};

export const useTenantList = (params: UseListParams) => {
  const { useList } = useTenantApi();
  return useList(params);
};
